/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */
import { getActualKey } from '../input/keys.js';
import useResizeObserver from './resizeObserver.js';

/**
 * @typedef {Object} JumpLinkOptions
 * @property {number} tabindex
 * @property {string} ariaLabel
 * @property {string|Element} redirectFocusNode
 */

const INTERACTIVE_ELEMENTS_SELECTOR = [
    'input',
    'button',
    'a',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="slider"]',
    '[role="menuitem"]',
    '[role="menuitemcheckbox"]',
    '[role="menuitemradio"]',
    '[role="switch"]',
    '[role="tab"]',
    '[role="tabpanel"]'
].join(',');

function createKeydownHanlder(node, redirectNode) {
    return (evt) => {
        const key = getActualKey(evt);

        if (key === 'space') {
            const firstInteractiveChild = (redirectNode ?? node).querySelector(INTERACTIVE_ELEMENTS_SELECTOR);
            firstInteractiveChild?.focus();
        }
    };
}

/**
 * Resizes node to redirectFocusNode sizing
 * @param {Element} redirectFocusNode
 * @param {Element} node
 */
function matchSize(redirectFocusNode, node) {
    const { top, left, width, height } = redirectFocusNode.getBoundingClientRect();
    node.style.top = `${top}px`;
    node.style.left = `${left}px`;
    node.style.width = `${width}px`;
    node.style.height = `${height}px`;
}

/**
 * Creates jump link group as specified in https://oat-sa.atlassian.net/wiki/spaces/UX/pages/1924857857/Resource+listing+screen#Jump-links
 * On space keypress jump link group focuses first interactive child
 * @param {Element} node
 * @param {JumpLinkOptions} options
 * @returns {Object}
 */
export default function jumpLink(node, options = {}) {
    if (!node.matches(INTERACTIVE_ELEMENTS_SELECTOR)) {
        node.setAttribute('tabindex', options.tabindex ?? 0);
    }
    node.setAttribute('aria-label', options.ariaLabel);

    const redirectFocusNode = options.redirectFocusNode instanceof Element
        ? options.redirectFocusNode
        : document.querySelector(options.redirectFocusNode);

    let resizeObserverAction;
    if (redirectFocusNode) {
        resizeObserverAction = useResizeObserver(redirectFocusNode, { callback: () => {
            // request animation frame to avoid "ResizeObserver loop completed with undelivered notifications" error
            window.requestAnimationFrame(() => {
                matchSize(redirectFocusNode, node);
            });
        }});
        node.style.position = 'absolute';
        node.style.pointerEvents = 'none';

        // avoid quirks with positioning inside relative position blocks
        node.remove();
        document.body.appendChild(node);
    }

    let keydownHandler = createKeydownHanlder(node, redirectFocusNode);
    node.addEventListener('keydown', keydownHandler);

    return {
        update() {
            node.removeEventListener('keydown', keydownHandler);
            keydownHandler = createKeydownHanlder(node);
            node.addEventListener('keydown', keydownHandler);
        },
        destroy() {
            resizeObserverAction?.destroy();
            node.removeEventListener('keydown', keydownHandler);
            if (redirectFocusNode) {
                node.remove();
            }
        }
    };
}